"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.boxWhiskerStdDevOutliers = exports.boxWhiskerIqrOutliers = exports.boxWhiskerStdDevValues = exports.boxWhiskerExtremumsValues = exports.boxWhiskerIqrValues = exports.BOX_WHISKER = void 0;
const cloneDeep_js_1 = __importDefault(require("lodash-es/cloneDeep.js"));
const factory_js_1 = require("../measures/factory.js");
/** @internal */
exports.BOX_WHISKER = {
    BOX_MIN_VALUE_NAME: 'Box Min',
    BOX_MEDIAN_VALUE_NAME: 'Box Median',
    BOX_MAX_VALUE_NAME: 'Box Max',
    WHISKER_MIN_VALUE_NAME: 'Whisker Min',
    WHISKER_MAX_VALUE_NAME: 'Whisker Max',
    OUTLIER_COUNT_VALUE_NAME: 'Outlier Count',
    OUTLIER_MIN_VALUE_NAME: 'Outlier Min',
    OUTLIER_MAX_VALUE_NAME: 'Outlier Max',
};
function boxWhiskerCommonValues(target) {
    return [
        (0, factory_js_1.customFormula)(exports.BOX_WHISKER.BOX_MIN_VALUE_NAME, 'QUARTILE([Attr], 1)', {
            Attr: target,
        }),
        (0, factory_js_1.customFormula)(exports.BOX_WHISKER.BOX_MEDIAN_VALUE_NAME, 'MEDIAN([Attr])', {
            Attr: target,
        }),
        (0, factory_js_1.customFormula)(exports.BOX_WHISKER.BOX_MAX_VALUE_NAME, 'QUARTILE([Attr], 3)', {
            Attr: target,
        }),
    ];
}
/**
 * Returns an array of values for box whisker plot using interquartile range (IQR) calculations.
 *
 * @param {Attribute} target - The target attribute for calculations.
 * @returns {Measure[]} An array of measures representing IQR values for box whisker plots.
 */
function boxWhiskerIqrValues(target) {
    return [
        ...boxWhiskerCommonValues(target),
        (0, factory_js_1.customFormula)(exports.BOX_WHISKER.WHISKER_MIN_VALUE_NAME, 'LOWERWHISKERMAX_IQR([Attr])', {
            Attr: target,
        }),
        (0, factory_js_1.customFormula)(exports.BOX_WHISKER.WHISKER_MAX_VALUE_NAME, 'UPPERWHISKERMIN_IQR([Attr])', {
            Attr: target,
        }),
        (0, factory_js_1.customFormula)(exports.BOX_WHISKER.OUTLIER_COUNT_VALUE_NAME, 'OUTLIERSCOUNT_IQR([Attr])', {
            Attr: target,
        }),
    ];
}
exports.boxWhiskerIqrValues = boxWhiskerIqrValues;
/**
 * Returns an array of extremum values for box whisker plot.
 *
 * @param {Attribute} target - The target attribute for calculations.
 * @returns {Measure[]} An array of measures representing extremum values for box whisker plots.
 */
function boxWhiskerExtremumsValues(target) {
    return [
        ...boxWhiskerCommonValues(target),
        (0, factory_js_1.customFormula)(exports.BOX_WHISKER.WHISKER_MIN_VALUE_NAME, 'MIN([Attr])', {
            Attr: target,
        }),
        (0, factory_js_1.customFormula)(exports.BOX_WHISKER.WHISKER_MAX_VALUE_NAME, 'MAX([Attr])', {
            Attr: target,
        }),
    ];
}
exports.boxWhiskerExtremumsValues = boxWhiskerExtremumsValues;
/**
 * Returns an array of values for box whisker plot using standard deviation calculations.
 *
 * @param {Attribute} target - The target attribute for calculations.
 * @returns {Measure[]} An array of measures representing standard deviation values for box whisker plots.
 */
function boxWhiskerStdDevValues(target) {
    return [
        ...boxWhiskerCommonValues(target),
        (0, factory_js_1.customFormula)(exports.BOX_WHISKER.WHISKER_MIN_VALUE_NAME, 'LOWERWHISKERMAX_STDEVP([Attr])', {
            Attr: target,
        }),
        (0, factory_js_1.customFormula)(exports.BOX_WHISKER.WHISKER_MAX_VALUE_NAME, 'UPPERWHISKERMIN_STDEVP([Attr])', {
            Attr: target,
        }),
        (0, factory_js_1.customFormula)(exports.BOX_WHISKER.OUTLIER_COUNT_VALUE_NAME, 'OUTLIERSCOUNT_STDEVP([Attr])', {
            Attr: target,
        }),
    ];
}
exports.boxWhiskerStdDevValues = boxWhiskerStdDevValues;
/**
 * Returns an attribute representing outlier points based on interquartile range (IQR) calculations.
 *
 * @param {Attribute} target - The target attribute for calculations.
 * @returns {Attribute} An attribute representing outliers for box whisker plots using IQR.
 */
const boxWhiskerIqrOutliers = (target) => {
    const outliersAttrWithInnerFilter = (0, cloneDeep_js_1.default)(target);
    const outliersMax = (0, factory_js_1.customFormula)(exports.BOX_WHISKER.OUTLIER_MAX_VALUE_NAME, '(UPPERWHISKERMIN_IQR([Attr]), all([Attr]))', {
        Attr: target,
    });
    const outliersMin = (0, factory_js_1.customFormula)(exports.BOX_WHISKER.OUTLIER_MIN_VALUE_NAME, '(LOWERWHISKERMAX_IQR([Attr]), all([Attr]))', {
        Attr: target,
    });
    outliersAttrWithInnerFilter.name = `${outliersAttrWithInnerFilter.name} (Outliers)`;
    outliersAttrWithInnerFilter.jaql = (nested) => {
        const jaql = Object.assign(Object.assign({}, target.jaql(true)), { filter: {
                or: [
                    {
                        fromNotEqual: outliersMax.jaql(true),
                    },
                    {
                        toNotEqual: outliersMin.jaql(true),
                    },
                ],
            } });
        return nested ? jaql : { jaql };
    };
    return outliersAttrWithInnerFilter;
};
exports.boxWhiskerIqrOutliers = boxWhiskerIqrOutliers;
/**
 * Returns an attribute representing outlier points based on standard deviation calculations.
 *
 * @param {Attribute} target - The target attribute for calculations.
 * @returns {Attribute} An attribute representing outliers for box whisker plots using standard deviation.
 */
const boxWhiskerStdDevOutliers = (target) => {
    const outliersAttrWithInnerFilter = (0, cloneDeep_js_1.default)(target);
    const outliersMax = (0, factory_js_1.customFormula)(exports.BOX_WHISKER.OUTLIER_MAX_VALUE_NAME, '(UPPERWHISKERMIN_STDEVP([Attr]), all([Attr]))', {
        Attr: target,
    });
    const outliersMin = (0, factory_js_1.customFormula)(exports.BOX_WHISKER.OUTLIER_MIN_VALUE_NAME, '(LOWERWHISKERMAX_STDEVP([Attr]), all([Attr]))', {
        Attr: target,
    });
    outliersAttrWithInnerFilter.jaql = (nested) => {
        const jaql = Object.assign(Object.assign({}, target.jaql(true)), { filter: {
                or: [
                    {
                        fromNotEqual: outliersMax.jaql(true),
                    },
                    {
                        toNotEqual: outliersMin.jaql(true),
                    },
                ],
            } });
        return nested ? jaql : { jaql };
    };
    return outliersAttrWithInnerFilter;
};
exports.boxWhiskerStdDevOutliers = boxWhiskerStdDevOutliers;
